import {
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { Link } from "react-router-dom";
import {
  getToolCategories,
  getToolsByCategory,
  toolsCatalog,
} from "../../data/toolsCatalog";
import Seo from "../../seo/Seo";
import {
  createBreadcrumbSchema,
  createOrganizationSchema,
} from "../../seo/utils";
import { ToolCard } from "./shared/ToolPageLayout";

const TOOL_PATH = "/tools";

function ToolsIndexPage() {
  const theme = useMantineTheme();
  const categories = getToolCategories();

  return (
    <>
      <Seo
        title="Free Student Tools | SubjectBuddy"
        description="Explore free SubjectBuddy student tools for grades, GPA, essays, speeches, presentations, and academic planning."
        path={TOOL_PATH}
        keywords={[
          "student tools",
          "semester grade calculator",
          "grade calculator",
          "final grade calculator",
          "letter grade calculator",
          "cumulative GPA calculator",
          "college essay editor",
          "speech word counter",
          "cursive generator",
          "ap bio score calculator",
          "apush score calculator",
          "racine carree calculator",
        ]}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOL_PATH },
          ]),
        ]}
      />

      <Box
        style={{
          background:
            "linear-gradient(135deg, rgba(235, 250, 242, 0.9) 0%, #ffffff 58%, rgba(238, 246, 255, 0.95) 100%)",
          borderBottom: "1px solid #e5efe7",
        }}
      >
        <Container size="xl" py={64}>
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Text>Tools</Text>
            </Breadcrumbs>

            <Group gap="sm">
              <Badge color="green" size="lg" radius="sm">
                Student Tools
              </Badge>
              <Badge color="blue" size="lg" radius="sm" variant="light">
                Free Resources
              </Badge>
            </Group>

            <Stack gap="sm" maw={820}>
              <Title order={1} c={theme.colors.green[8]}>
                Free Student Tools
              </Title>
              <Text size="lg" c="dimmed" style={{ lineHeight: 1.8 }}>
                Use SubjectBuddy tools to calculate grades, plan GPA goals,
                review essays, estimate speech timing, and make academic work
                easier to manage. Every tool is free, crawlable, and available
                without login.
              </Text>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <Stack gap={42}>
          {categories.map((category) => {
            const tools = getToolsByCategory(category);

            return (
              <section key={category}>
                <Stack gap="lg">
                  <Group justify="space-between" align="end">
                    <Stack gap={4}>
                      <Title order={2}>{category}</Title>
                      <Text c="dimmed">
                        {getCategoryDescription(category)}
                      </Text>
                    </Stack>
                    <Badge color="green" variant="light" radius="sm">
                      {tools.length} tools
                    </Badge>
                  </Group>
                  <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
                    {tools.map((tool) => (
                      <ToolCard key={tool.slug} tool={tool} />
                    ))}
                  </SimpleGrid>
                </Stack>
              </section>
            );
          })}
        </Stack>
      </Container>
    </>
  );
}

function getCategoryDescription(category: string) {
  if (category === "Grade & GPA Tools") {
    return "Calculate class grades, semester grades, final exam targets, letter grades, and cumulative GPA.";
  }

  if (category === "Essay & Writing Tools") {
    return "Review essays, edit drafts, and improve writing clarity with ethical student-focused tools.";
  }

  if (category === "Speech & Presentation Tools") {
    return "Estimate speech word counts, speaking time, and presentation length before rehearsal.";
  }

  if (category === "Math Tools") {
    return "Calculate percentages, square roots, fractions, scientific expressions, and algebra formulas.";
  }

  if (category === "AP Score Calculators") {
    return "Estimate AP practice scores and compare AP grading scale guidance across popular exams.";
  }

  return `${toolsCatalog.length} free tools for students.`;
}

export default ToolsIndexPage;
